import { onScopeDispose, ref, watch } from 'vue'
import type { Ref } from 'vue'

import Network from '@/Network'
import { getApiErrorMessage } from '@/utils/apiError'

export const DOCUMENT_FILE_ERROR = 'Não foi possível carregar o arquivo do documento'

/**
 * Baixa o PDF da versão (marca d'água gravada pelo backend, D28) e expõe um object URL para o
 * `PdfRenderer`. Revoga a URL anterior a cada troca e ao descartar o escopo; a resposta de uma
 * versão antiga nunca sobrescreve a atual.
 */
export function useDocumentFileUrl(versionId: Ref<number | null>) {
  const url = ref<string | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  let requestId = 0

  const revoke = () => {
    if (url.value) URL.revokeObjectURL(url.value)
    url.value = null
  }

  watch(
    versionId,
    async (id) => {
      const current = ++requestId
      revoke()
      error.value = null
      isLoading.value = id !== null
      if (id === null) return
      try {
        const blob = await Network.professionalDocuments.getVersionFileBlob(id)
        if (current === requestId) url.value = URL.createObjectURL(blob)
      } catch (e) {
        if (current === requestId) error.value = getApiErrorMessage(e, DOCUMENT_FILE_ERROR)
      } finally {
        if (current === requestId) isLoading.value = false
      }
    },
    { immediate: true }
  )

  onScopeDispose(() => {
    requestId++
    revoke()
  })

  return { url, isLoading, error }
}
